import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import ToastContainer from './components/ui/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import CookieConsent from './components/CookieConsent';

import Login from './pages/Login';
import Legal from './pages/Legal';
import IssuesDashboard from './pages/IssuesDashboard';
import AuditViewer from './pages/AuditViewer';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseForm from './pages/CourseForm';
import MyCourses from './pages/MyCourses';
import Employees from './pages/Employees';
import EmployeeForm from './pages/EmployeeForm';
import AssignCourses from './pages/AssignCourses';
import TrainingPage from './pages/TrainingPage';
import TestPage from './pages/TestPage';
import MyRoute from './pages/MyRoute';
import MyProfile from './pages/MyProfile';
import SignaturePage from './pages/SignaturePage';
import CourseContent from './pages/CourseContent';
import TemplateDetails from './pages/TemplateDetails';
import MyCertificates from './pages/MyCertificates';
import ForgotPassword from './pages/ForgotPassword';
import TrainingDetails from './pages/TrainingDetails';
import Companies from './pages/Companies';
import CompanyForm from './pages/CompanyForm';
import CourseStats from './pages/CourseStats';
import CompanyDocuments from './pages/CompanyDocuments';
import CompanyDetails from './pages/CompanyDetails';
import CompanyCompliance from './pages/CompanyCompliance';
import EmployeeSheets from './pages/EmployeeSheets';
import Notifications from './pages/Notifications';
import SearchCompanies from './pages/SearchCompanies';
import SearchCompanyDetails from './pages/SearchCompanyDetails';
import SSMCompanies from './pages/SSMCompanies';
import GlobalReports from './pages/GlobalReports';
import SearchEmployees from './pages/SearchEmployees';
import Departments from './pages/Departments';
import EmployeeDetails from './pages/EmployeeDetails';
import IPDashboard from './pages/IPDashboard';
import Manual from './pages/Manual';
import JobDescriptionTemplates from './pages/JobDescriptionTemplates';
import SignJobDescription from './pages/SignJobDescription';

// Roluri care pot parcurge cursuri și își au propriile certificate.
// SSM-iștii sunt incluși fiindcă pot face cursuri ei înșiși (refresh certificare, IIG/ILM proprii etc.).
// SUPER_ADMIN exclus — e team intern al platformei, nu face instruiri SSM personale.
const COURSE_ROLES = [
  'EMPLOYEE',
  'COMPANY_ADMIN',
  'DIRECTOR_GENERAL',
  'SSM_COMPANY_ADMIN',
  'SSM_INSTRUCTOR',
];
// Roluri admin SSM
const SSM_ROLES = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'];
// Toți adminii
const ALL_ADMIN = [
  'SUPER_ADMIN',
  'SSM_COMPANY_ADMIN',
  'SSM_INSTRUCTOR',
  'COMPANY_ADMIN',
  'DIRECTOR_GENERAL',
];
// Toate rolurile autentificate (pentru pagini comune: profil, traseu)
const ALL_ROLES = [
  'SUPER_ADMIN',
  'SSM_COMPANY_ADMIN',
  'SSM_INSTRUCTOR',
  'COMPANY_ADMIN',
  'DIRECTOR_GENERAL',
  'EMPLOYEE',
];

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <ToastContainer />
          <Router>
            <Routes>
              {/* PUBLIC */}
              <Route path="/login" element={<Login />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/legal/:tab" element={<Legal />} />
              {/* SECURITATE: înregistrarea publică a fost eliminată — crea conturi SUPER_ADMIN fără
              control. Crearea conturilor se face din aplicație (angajați) sau prin seed. */}
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* DASHBOARD */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/issues"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <IssuesDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Audit log: doar rolurile administrative (match cu backend audit.routes.js:8). Anterior fara restrictie → GDPR breach. */}
              <Route
                path="/audit"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'SUPER_ADMIN',
                      'SSM_COMPANY_ADMIN',
                      'SSM_INSTRUCTOR',
                      'COMPANY_ADMIN',
                    ]}
                  >
                    <Layout>
                      <AuditViewer />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard-ip"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <IPDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ CURSURI ═══ */}
              {/* /courses și /courses/new permit și EMPLOYEE — CLM-urile pot crea/edita cursurile lor (validat în controller) */}
              <Route
                path="/courses"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <Courses />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/new"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <CourseForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:id/edit"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <CourseForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/templates/:templateId"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <TemplateDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:courseId/content"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <CourseContent />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:courseId/stats"
                element={
                  <ProtectedRoute allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN']}>
                    <Layout>
                      <CourseStats />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ CURSURILE MELE — EMPLOYEE + COMPANY_ADMIN ═══ */}
              <Route
                path="/my-courses"
                element={
                  <ProtectedRoute allowedRoles={COURSE_ROLES}>
                    <Layout>
                      <MyCourses />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ TRAINING — cu courseId în URL ═══
              TrainingPage primește courseId, face POST /start, obține trainingId de la server.
              NU folosi trainingId în URL pentru start — cauzează 404 pe /trainings/undefined. */}
              <Route
                path="/training/:courseId"
                element={
                  <ProtectedRoute allowedRoles={COURSE_ROLES}>
                    <Layout>
                      <TrainingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Test — cu trainingId (obținut DUPĂ start) */}
              <Route
                path="/training/:trainingId/test"
                element={
                  <ProtectedRoute allowedRoles={COURSE_ROLES}>
                    <Layout>
                      <TestPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Semnătură — cu trainingId */}
              <Route
                path="/training/:trainingId/sign"
                element={
                  <ProtectedRoute allowedRoles={[...COURSE_ROLES, ...SSM_ROLES]}>
                    <Layout>
                      <SignaturePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/training-details/:trainingId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TrainingDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-certificates"
                element={
                  <ProtectedRoute allowedRoles={COURSE_ROLES}>
                    <Layout>
                      <MyCertificates />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ ANGAJAȚI ═══ */}
              <Route
                path="/employees"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <Employees />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/new"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'SUPER_ADMIN',
                      'SSM_COMPANY_ADMIN',
                      'SSM_INSTRUCTOR',
                      'COMPANY_ADMIN',
                      'EMPLOYEE',
                    ]}
                  >
                    <Layout>
                      <EmployeeForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <EmployeeForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <EmployeeDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ ATRIBUIRE CURSURI ═══ */}
              {/* CLM-urile (rol EMPLOYEE) pot atribui ILM/IP/SUPL_SSM — validat în controller */}
              <Route
                path="/assign-courses"
                element={
                  <ProtectedRoute allowedRoles={[...ALL_ADMIN, 'EMPLOYEE']}>
                    <Layout>
                      <AssignCourses />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ PROFIL & SETĂRI — toți utilizatorii autentificați ═══ */}
              <Route
                path="/my-profile"
                element={
                  <ProtectedRoute allowedRoles={ALL_ROLES}>
                    <Layout>
                      <MyProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-route"
                element={
                  <ProtectedRoute allowedRoles={ALL_ROLES}>
                    <Layout>
                      <MyRoute />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ FIRME ═══ */}
              <Route
                path="/ssm-companies"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <Layout>
                      <SSMCompanies />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute allowedRoles={SSM_ROLES}>
                    <Layout>
                      <Companies />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/new"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'SSM_COMPANY_ADMIN']}>
                    <Layout>
                      <CompanyForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <CompanyDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/departments"
                element={
                  <ProtectedRoute
                    allowedRoles={[...SSM_ROLES, 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}
                  >
                    <Layout>
                      <Departments />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId/documents"
                element={
                  <ProtectedRoute allowedRoles={ALL_ADMIN}>
                    <Layout>
                      <CompanyDocuments />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Conformitate firmă client — accesibilă din PortfolioDashboard de către SSM-iști */}
              <Route
                path="/companies/:companyId/compliance"
                element={
                  <ProtectedRoute
                    allowedRoles={['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR']}
                  >
                    <Layout>
                      <CompanyCompliance />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId/sheets"
                element={
                  <ProtectedRoute allowedRoles={ALL_ADMIN}>
                    <Layout>
                      <EmployeeSheets />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ FIȘE SSM — doar fluxul de semnătură (notificări, dashboard) ═══ */}
              <Route
                path="/sheets-to-sign"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'SSM_INSTRUCTOR',
                      'COMPANY_ADMIN',
                      'SSM_COMPANY_ADMIN',
                      'DIRECTOR_GENERAL',
                      'EMPLOYEE',
                    ]}
                  >
                    <Layout>
                      <EmployeeSheets />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ CĂUTARE ═══ */}
              <Route
                path="/search"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <Layout>
                      <SearchCompanies />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search/company/:companyId"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <Layout>
                      <SearchCompanyDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search/employees"
                element={
                  <ProtectedRoute allowedRoles={['COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE']}>
                    <Layout>
                      <SearchEmployees />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ RAPOARTE ═══ */}
              {/* COMPANY_ADMIN + DIRECTOR_GENERAL pot vedea raportul ITM pentru firma lor.
              Scoping-ul pe firmă e gestionat backend (alt agent). */}
              <Route
                path="/reports"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'SUPER_ADMIN',
                      'SSM_COMPANY_ADMIN',
                      'SSM_INSTRUCTOR',
                      'COMPANY_ADMIN',
                      'DIRECTOR_GENERAL',
                    ]}
                  >
                    <Layout>
                      <GlobalReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ FIȘA POSTULUI — anexă bilingvă SSM/SU ═══ */}
              <Route
                path="/job-descriptions/templates"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'SSM_COMPANY_ADMIN',
                      'SSM_INSTRUCTOR',
                      'COMPANY_ADMIN',
                      'DIRECTOR_GENERAL',
                      'EMPLOYEE',
                    ]}
                  >
                    <Layout>
                      <JobDescriptionTemplates />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-descriptions/:instanceId/sign"
                element={
                  <ProtectedRoute allowedRoles={ALL_ROLES}>
                    <Layout>
                      <SignJobDescription />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ NOTIFICĂRI ═══ */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Notifications />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ═══ MANUAL DE UTILIZARE ═══ */}
              <Route
                path="/manual"
                element={
                  <ProtectedRoute allowedRoles={ALL_ROLES}>
                    <Layout>
                      <Manual />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* REDIRECTS */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <CookieConsent />
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
